/**
 * PublishPanel — sdílená komponenta pro publish/unpublish + SSO toggle.
 *
 * JEDEN společný panel pro všechny moduly (apphost, supabase, dbhost, webhost,
 * orchestrator, ...). Architektura: viz wiki `sso-publish-architecture`.
 *
 * Public toggle volá owner tool. Projektová instance používá resource-qualified
 * adresu `PRJ-XXXXXXXX.{module}.publish|unpublish`, kterou proxy rozřeší přes
 * orchestrátor. Browser nikdy nevolá obecný network publish ani neposílá port.
 *
 * Stavy:
 *   syncing (desired≠actual)            → spinner
 *   published (actual=true)             → zelený badge + "Skrýt" + SSO toggle
 *   unpublished (actual=false)          → "Publikovat" tlačítko + form
 *
 * SSO toggle se zobrazí jen když:
 *   - supportsSso=true (modul podporuje OIDC, např. supabase Studio, dashboard)
 *   - published=true (proxy host existuje, jinak by KC client měl redirectUri
 *     na neexistující doménu).
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { mcpCall } from '../../lib/mcp';
import { Button } from "./Button";
import { Modal } from "./Modal";
import { ConfirmDialog } from './ConfirmDialog';
import { AsyncStatusToggle } from './AsyncStatusToggle';
import { Fail2banReactionToggle } from './Fail2banReactionToggle';
import { ExternalLink, Globe, Loader2, Lock, Shield, Unlock } from 'lucide-react';
import { toast } from 'sonner';

interface PublishPanelProps {
  module: string;
  instance: string;
  port: number;
  /** Orchestrator project binding. Obě hodnoty jsou povinné společně. */
  projectId?: string;
  serviceKey?: string;
  /** Cíl publikování — 'app' pro apphost, 'api'/'studio' pro supabase */
  target?: string;
  currentDomain?: string | null;
  published?: boolean;
  syncing?: boolean;
  /** Modul podporuje SSO (Studio, dashboard) — zobrazí druhý toggle pod public */
  supportsSso?: boolean;
  ssoEnabled?: boolean;
  ssoRegistered?: boolean;
  ssoClientId?: string | null;
  /** Modul podporuje API block na veřejné URL (= dashboard, ne api endpoint). */
  supportsApiBlock?: boolean;
  /** Aktuální stav api_blocked toggle z catalog extras */
  apiBlocked?: boolean;
  onChanged?: () => void;
  /** Adresa a Skrýt jsou jinde (páska nahoře) — tady zůstanou jen SSO / API. */
  hideAddress?: boolean;
}

interface PublishResult {
  ok: boolean;
  error?: string;
  hint?: string;
  action_required?: string;
}

export function PublishPanel(
  {
    module: mod,
    instance,
    port,
    projectId,
    serviceKey,
    target,
    currentDomain,
    published,
    syncing,
    supportsSso,
    ssoEnabled,
    ssoRegistered,
    ssoClientId,
    supportsApiBlock,
    apiBlocked,
    onChanged,
    hideAddress = false,
  }: PublishPanelProps,
) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [customDomain, setCustomDomain] = useState('');
  const [failedResults, setFailedResults] = useState<Record<string, PublishResult> | null>(null);
  const [hideConfirm, setHideConfirm] = useState(false);

  const publicToggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const action = enabled ? 'publish' : 'unpublish';
      let toolName: string;
      let toolArgs: Record<string, unknown>;
      if (projectId || serviceKey) {
        if (!projectId || !serviceKey) {
          throw new Error(t('ui.incompleteBinding', 'Neúplný orchestrator project binding'));
        }
        toolName = `${projectId}.${mod}.${action}`;
        toolArgs = { service: serviceKey };
      } else if (mod === 'acm-self') {
        toolName = `acm-self.${action}`;
        toolArgs = { target: instance.replace(/^acm-/, '') };
      } else {
        toolName = `${mod}.${action}`;
        toolArgs = { name: instance };
      }
      if (enabled && customDomain) toolArgs.domain = customDomain;
      if (target) toolArgs.target = target;
      return await mcpCall<Record<string, unknown>>(toolName, toolArgs);
    },
    onSuccess: (r: Record<string, unknown>, enabled: boolean) => {
      const results = r.results as Record<string, PublishResult> | undefined;
      const partial = r.partial === true;
      const failed = (r.failed as string[] | undefined) ?? [];

      if (r.ok === true) {
        toast.success(enabled
          ? t('ui.publishStarted', 'Publikování spuštěno')
          : t('ui.hideStarted', 'Skrývání spuštěno'));
        setShowForm(false);
        onChanged?.();
      } else if (partial && results) {
        // Partial failure — některé cíle prošly, jiné ne. Otevři modal s detaily.
        setFailedResults(results);
        toast.error(
          t('ui.publishPartialFail', 'Publish částečně selhal: {{failed}} (klik na ikonku detailu)', {
            failed: failed.join(', '),
          }),
        );
        onChanged?.();
      } else {
        toast.error((r.error as string) ?? t('widgets.error', 'Chyba'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const { data: kcData } = useQuery({
    queryKey: ['kc-instances-availability'],
    queryFn: () => api.mcp<{ ok: boolean; count: number }>('sso.keycloak_list_instances', {}),
    staleTime: 5 * 60_000,
  });
  const kcAvailable = (kcData?.count ?? 0) > 0;

  const apiBlockToggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.mcp('network.set_api_blocked', { domain: currentDomain, enabled }),
    onSuccess: (r) => {
      if (r?.ok) {
        toast.success(r.api_blocked ? t('ui.apiBlocked', 'API blokováno') : t('ui.apiAllowed', 'API povoleno'));
        onChanged?.();
      } else {
        toast.error((r as { error?: string })?.error ?? t('widgets.error', 'Chyba'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const ssoToggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.mcp('network.set_sso', { domain: currentDomain, enabled }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          r.sso_registered
            ? t('ui.ssoRegistered', 'SSO zaregistrováno v Keycloaku')
            : t('ui.ssoChanged', 'SSO stav změněn'),
        );
        onChanged?.();
      } else {
        toast.error(r.error ?? t('widgets.error', 'Chyba'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const loading = publicToggle.isPending || syncing;

  // Syncing stav — desired≠actual
  if (syncing && !publicToggle.isPending) {
    return (
      <div className='flex items-center gap-3 p-3 rounded-xl border border-blue-500/20 bg-blue-500/5'>
        <Loader2 size={16} className='text-blue-400 animate-spin' />
        <span className='text-sm text-blue-400 flex-1'>{t('ui.syncing', 'Synchronizuji…')}</span>
        {currentDomain && <span className='text-xs text-gray-500 font-mono'>{currentDomain}</span>}
      </div>
    );
  }

  // Published — actual=true → zobraz public stav + SSO toggle (pokud supportsSso)
  if (published && currentDomain) {
    return (
      <div className='space-y-2'>
        {!hideAddress && (
        <div className='flex items-center gap-3 p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5'>
          <Globe size={16} className='text-emerald-400' />
          <div className='flex-1'>
            <a
              href={`https://${currentDomain}`}
              target='_blank'
              rel='noopener'
              className='text-sm text-emerald-400 hover:text-emerald-300 font-mono flex items-center gap-1'
            >
              {currentDomain} <ExternalLink size={10} />
            </a>
            <div className='text-[10px] text-gray-500'>
              {t('ui.publiclyAccessible', 'Veřejně přístupné')}
            </div>
          </div>
          <Button
            variant='ghost'
            size='sm'
            loading={loading}
            onClick={() => setHideConfirm(true)}
          >
            <Lock size={12} /> {t('ui.hide', 'Skrýt')}
          </Button>
        </div>
        )}

        {supportsSso && kcAvailable && (
          <div className='px-3 py-2 rounded-xl border border-gray-800 bg-gray-900/30'>
            <AsyncStatusToggle
              value={ssoEnabled ?? false}
              actual={ssoRegistered ?? false}
              pending={ssoToggle.isPending}
              onToggle={(v) => ssoToggle.mutateAsync(v)}
              onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
              renderIcon={(on) => (
                <Shield size={12} className={on ? 'text-blue-400' : 'text-gray-600'} />
              )}
              label={
                <div className='flex flex-col'>
                  <span className='text-[11px] text-gray-300'>
                    {t('ui.ssoFromOutside', 'Keycloak SSO z venku')}
                    {ssoRegistered && ssoClientId ? (
                      <span className='font-mono text-gray-500 ml-1'>({ssoClientId})</span>
                    ) : null}
                  </span>
                  <span className='text-[10px] text-gray-600'>
                    {t(
                      'ui.ssoLoginFallback',
                      'Login form je vždy dostupný jako fallback. Z LAN/VPN je login form primární.',
                    )}
                  </span>
                </div>
              }
            />
          </div>
        )}

        {supportsApiBlock && (
          <div className='px-3 py-2 rounded-xl border border-gray-800 bg-gray-900/30'>
            <AsyncStatusToggle
              value={apiBlocked ?? false}
              actual={apiBlocked ?? false}
              pending={apiBlockToggle.isPending}
              onToggle={(v) => apiBlockToggle.mutateAsync(v)}
              onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
              renderIcon={(on) => (
                <Lock size={12} className={on ? 'text-amber-400' : 'text-gray-600'} />
              )}
              label={
                <div className='flex flex-col'>
                  <span className='text-[11px] text-gray-300'>
                    {t('ui.blockApiFromOutside', 'Blokovat API z venku')}
                  </span>
                  <span className='text-[10px] text-gray-600'>
                    {t(
                      'ui.blockApiHint',
                      '/api, /mcp, /manage, /apiext → 404. UI + /auth + SPA assets fungují. Bearer API pak jen z LAN/VPN.',
                    )}
                  </span>
                </div>
              }
            />
          </div>
        )}
        {/* Reakce fail2ban — jeden sdílený přepínač všude, kde se zapíná veřejný přístup. */}
        <Fail2banReactionToggle variant='publish-row' />
      </div>
    );
  }

  // Unpublished — show toggle (+ NPM warning if applicable)
  if (!showForm) {
    return (
      <div className='space-y-2'>
        <div className='flex items-center gap-3 p-3 rounded-xl border border-gray-800'>
          <Unlock size={16} className='text-gray-600' />
          <span className='text-sm text-gray-500 flex-1'>
            {t('ui.internalOnly', 'Pouze interní přístup')}
            {port > 0 ? ` (:${port})` : ''}
          </span>
          <Button
            variant='primary'
            size='sm'
            onClick={() => setShowForm(true)}
          >
            <Globe size={12} /> {t('ui.publish', 'Publikovat')}
          </Button>
        </div>
      </div>
    );
  }

  // Publish form
  const inp =
    'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  return (
    <div className='p-3 rounded-xl border border-blue-500/20 bg-blue-500/5 space-y-3'>
      <div className='text-xs font-semibold text-blue-400'>
        {t('ui.publishService', 'Publikovat službu')}
      </div>
      <div>
        <label className='text-[10px] text-gray-500 mb-1 block'>
          {t('ui.customDomainOptional', 'Vlastní doména (volitelné — jinak se vygeneruje automaticky)')}
        </label>
        <input
          value={customDomain}
          onChange={(e) => setCustomDomain(e.target.value)}
          placeholder={t('ui.autoDomainPlaceholder', 'Nechte prázdné pro automatickou adresu')}
          className={inp + ' font-mono'}
        />
      </div>
      <div className='flex gap-2'>
        <Button variant='ghost' size='sm' onClick={() => setShowForm(false)}>
          {t('common.cancel', 'Zrušit')}
        </Button>
        <Button
          variant='primary'
          size='sm'
          loading={loading}
          onClick={() => publicToggle.mutate(true)}
        >
          {loading ? <Loader2 size={12} className='animate-spin' /> : <Globe size={12} />}
          {t('ui.publish', 'Publikovat')}
        </Button>
      </div>
      {/* Modal s per-target detaily při partial fail */}
      {failedResults && (
        <Modal
          open={!!failedResults}
          onClose={() => setFailedResults(null)}
          title={t('ui.publishDetail', 'Publish — detail per cíl')}
        >
          <div className='space-y-2 max-h-[60vh] overflow-y-auto'>
            {Object.entries(failedResults).map(([target, r]) => (
              <div
                key={target}
                className={`border rounded p-3 ${r.ok ? 'border-emerald-700 bg-emerald-950/20' : 'border-amber-700 bg-amber-950/20'}`}
              >
                <div className='flex items-center gap-2 mb-1'>
                  <span className={`text-xs font-mono font-semibold ${r.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {r.ok ? '✓' : '✗'} {target}
                  </span>
                </div>
                {r.hint && (
                  <div className='text-xs text-gray-300 mb-1'>
                    <strong>{t('ui.whatHappened', 'Co se stalo:')}</strong> {r.hint}
                  </div>
                )}
                {r.action_required && (
                  <div className='text-xs text-gray-400 whitespace-pre-wrap bg-gray-950 border border-gray-800 rounded p-2 mt-1'>
                    <strong className='text-gray-300'>{t('ui.whatToDo', 'Co dělat:')}</strong>
                    {'\n'}
                    {r.action_required}
                  </div>
                )}
                {r.error && !r.hint && (
                  <div className='text-[10px] text-gray-600 font-mono mt-1 break-all'>
                    {r.error.slice(0, 300)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
      {/* Modal s per-target detaily při partial fail */}
      {failedResults && (
        <Modal
          open={!!failedResults}
          onClose={() => setFailedResults(null)}
          title={t('ui.publishDetail', 'Publish — detail per cíl')}
        >
          <div className='space-y-2 max-h-[60vh] overflow-y-auto'>
            {Object.entries(failedResults).map(([target, r]) => (
              <div
                key={target}
                className={`border rounded p-3 ${r.ok ? 'border-emerald-700 bg-emerald-950/20' : 'border-amber-700 bg-amber-950/20'}`}
              >
                <div className='flex items-center gap-2 mb-1'>
                  <span className={`text-xs font-mono font-semibold ${r.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {r.ok ? '✓' : '✗'} {target}
                  </span>
                </div>
                {r.hint && (
                  <div className='text-xs text-gray-300 mb-1'>
                    <strong>{t('ui.whatHappened', 'Co se stalo:')}</strong> {r.hint}
                  </div>
                )}
                {r.action_required && (
                  <div className='text-xs text-gray-400 whitespace-pre-wrap bg-gray-950 border border-gray-800 rounded p-2 mt-1'>
                    <strong className='text-gray-300'>{t('ui.whatToDo', 'Co dělat:')}</strong>
                    {'\n'}
                    {r.action_required}
                  </div>
                )}
                {r.error && !r.hint && (
                  <div className='text-[10px] text-gray-600 font-mono mt-1 break-all'>
                    {r.error.slice(0, 300)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={hideConfirm}
        title={t('ui.hide', 'Skrýt')}
        description={t('ui.hideDomainConfirm', 'Skrýt {{domain}}?', { domain: currentDomain ?? '' })}
        confirmLabel={t('ui.hide', 'Skrýt')}
        cancelLabel={t('ui.cancel', 'Zrušit')}
        danger
        onConfirm={() => {
          setHideConfirm(false);
          publicToggle.mutate(false);
        }}
        onCancel={() => setHideConfirm(false)}
      />
    </div>
  );
}
